import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";
import { getStorage, setStorage, unsetStorage } from "@helpers/storage";
import logger from "@helpers/logger";
import { router } from "@router/router";
import { Api } from "@helpers/api";
import { adminOverrideEnabled } from "@helpers/session";
import { useDisplayStore } from "@stores/displayStore";

export const useUserStore = defineStore("user", {
    actions: {
        initialize(yhub_token = null) {
            // Attach displaystore to userstore
            const displayStore = useDisplayStore();
            this.stores.displayStore = displayStore;

            // First we load tokens from storage
            const tokens = getStorage("tokens");
            this.tokens = tokens || {};

            // If we received a yhub_token, set it and redirect to dashboard
            if (yhub_token) {
                this.setToken("yhub", yhub_token);
            }

            // If we have a token, we try to login the user
            if (this.tokens["yhub"]) {
                return this.fetchIdentity();
            }

            // Mark the userStore as loaded
            this.loaded = true;
        },
        can(permission) {
            const permissions = this.permissions;

            if (permissions.includes(permission)) {
                return true;
            }

            // Wildcard support
            if (permission.endsWith(".*")) {
                const basePermission = permission.slice(0, -2);

                return permissions.some((p) => p.startsWith(basePermission));
            }

            return this.adminOverrideEnabled;
        },
        fetchIdentity() {
            // Fetch user identity from API
            Api.get("/user")
                .then((response) => {
                    this.setUser(
                        response.data.data.user,
                        response.data.data.customers,
                        response.data.data.permissions,
                    );
                })
                .catch(() => {
                    logger.warn("Failed fetching user identity, logging out.");
                    this.loaded = true;
                    this.logout();
                });
        },
        revokeToken(name) {
            const revokeEndpoints = {
                yhub: "/user/logout",
            };

            if (revokeEndpoints[name]) {
                Api.post(revokeEndpoints[name]).catch(() => {
                    logger.warn(`Failed sending revoke request for ${name}.`);
                });
            }

            this.setToken(name, null);

            if (name === "yhub") {
                // Unset permissions after sometime to ensure clean client states.
                unsetStorage("permissions");

                this.customers = [];
                this.employee = null;
                this.firstname = null;
                this.user_id = null;
                this.lastname = null;
                this.preposition = null;
                this.selectedCustomerId = undefined;
                this.username = null;
                this.permissions = [];
            }
        },
        logout(manual = false) {
            // Send logout request
            Api.post("/user/logout").catch(() => {
                logger.warn("Failed sending logout request.");
            });

            // Unset all storage items for userdetails
            this.revokeToken("yhub");
            unsetStorage("tokens");

            if (manual) {
                // Navigate to SSO logout page with login url as return path
                window.location.href =
                    // eslint-disable-next-line no-undef
                    ENV_VAR.sso_server + "/logout?return=" + window.location.origin + "/login";
                return;
            }

            // Navigate to the login page
            router.push({ name: "/auth/login" });
        },
        setActiveCustomerId(id) {
            this.selectedCustomerId = id;
            setStorage("selectedCustomerId", id);
        },
        setTracker(tracker) {
            this.tracker = tracker;
        },
        setToken(name, token) {
            if (token === null) {
                delete this.tokens[name];
            } else {
                this.tokens[name] = token;
            }

            setStorage("tokens", this.tokens);
        },
        setUser(user, customers, permissions) {
            this.firstname = user.firstname;
            this.lastname = user.lastname;
            this.preposition = user.preposition;
            this.username = user.username;
            this.employee = user.is_employee;
            this.user_id = user.id;

            this.customers = customers;
            this.permissions = permissions;

            // Set default
            this.selectedCustomerId = null;

            // Set customer id
            if (this.customers.length > 0) {
                // Get the selected customer id from storage
                const selectedCustomerId = getStorage("selectedCustomerId");

                // Set the selected customer id if set
                if (selectedCustomerId) {
                    this.selectedCustomerId = selectedCustomerId / 1;
                }

                // Set the first customer id if not set when user is not an employee
                if (!selectedCustomerId && !this.isEmployee) {
                    this.selectedCustomerId = this.customers[0].id;
                }
            }

            // Set tracker user id
            if (this.tracker) {
                this.tracker.setUserID(user.username);
            }

            // Set Sentry user
            Sentry.setUser({
                id: user.username,
                username: this.getFullname,
            });

            // Mark the userStore as loaded
            this.loaded = true;

            try {
                const current = router.currentRoute;
                const currentPathName = current.value.name ?? window.location.path;
                if (currentPathName.startsWith("/auth/")) {
                    router.push("/");
                }
            } catch (e) {}
        },
        getToken(name) {
            return this.tokens[name];
        },
    },
    getters: {
        adminOverrideEnabled() {
            if (this.permissions.includes("admin.override.all") && adminOverrideEnabled()) {
                return true;
            }

            return false;
        },
        async getActiveUserType() {
            if (!this.loaded || !this.stores.displayStore.loaded) {
                await new Promise((resolve) => {
                    const interval = setInterval(() => {
                        if (this.loaded && this.stores.displayStore.loaded) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 50);
                });
            }

            return this.rawActiveUserType;
        },
        rawActiveUserType() {
            if (this.stores.displayStore.uuid && this.stores.displayStore.verified_at) {
                return "display";
            }

            if (this.selectedCustomerId === null) {
                return "employee";
            }

            if (this.selectedCustomerId !== undefined) {
                return "customer";
            }

            return "guest";
        },
        getActiveCustomer() {
            return this.customers.find((customer) => customer.id === this.selectedCustomerId);
        },
        getCompanyName() {
            if (this.getActiveCustomer) {
                return this.getActiveCustomer.name;
            }

            // eslint-disable-next-line no-undef
            return ENV_VAR.entityname;
        },
        getFirstname() {
            return this.firstname || "Guest";
        },
        getFullname() {
            if (!this.preposition) {
                return `${this.getFirstname} ${this.getLastname}`;
            }

            return `${this.getFirstname} ${this.preposition} ${this.getLastname}`;
        },
        getLastname() {
            return this.lastname || "User";
        },
        isEmployee() {
            return this.employee;
        },
        isLoggedin() {
            return this.username !== null && this.firstname !== null && this.lastname !== null;
        },
    },
    state: () => ({
        customers: [],
        permissions: [],
        user_id: null,
        employee: true,
        firstname: null,
        lastname: null,
        loaded: false,
        preposition: null,
        selectedCustomerId: undefined,
        tracker: null,
        tokens: {},
        username: null,
        stores: {},
    }),
});
